<template>
  <div class="management-potential-levels-chart">
    <h3 v-if="title" class="management-potential-levels-chart__title">
      {{ title }}
    </h3>

    <div class="management-potential-levels-chart__body">
      <canvas ref="chartCanvas" />
    </div>
  </div>
</template>

<script>
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  Tooltip,
} from 'chart.js'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip
)

export default {
  name: 'ManagementPotentialLevelsChart',

  props: {
    title: {
      type: String,
      default: '',
    },

    items: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    const chartInstance = ref(null)

    const destroyChart = () => {
      if (chartInstance.value) {
        chartInstance.value.destroy()
        chartInstance.value = null
      }
    }

    const renderChart = () => {
      destroyChart()

      if (!chartCanvas.value) {
        return
      }

      const labels = props.items.map((item) => item.label)
      const values = props.items.map((item) => Number(item.value) || 0)
      const colors = props.items.map((item) => item.color || '#D5DBE2')

      chartInstance.value = new Chart(chartCanvas.value, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderRadius: 4,
              borderSkipped: false,
              minBarLength: 2,
              barPercentage: 0.94,
              categoryPercentage: 0.94,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              display: false,
            },
            datalabels: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => `${context.parsed.y}`,
              },
            },
          },
          scales: {
            x: {
              grid: {
                color: 'rgba(0, 0, 0, 0.04)',
                drawBorder: false,
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
              border: {
                display: false,
              },
            },
            y: {
              beginAtZero: true,
              suggestedMax: Math.max(...values, 0) + 5,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false,
              },
              ticks: {
                precision: 0,
                color: 'rgba(0, 0, 0, 0.55)',
                font: {
                  size: 12,
                },
              },
              border: {
                display: false,
              },
            },
          },
        },
        plugins: [
          {
            id: 'valueLabels',
            afterDatasetsDraw(chart) {
              const { ctx } = chart

              chart.data.datasets.forEach((dataset, datasetIndex) => {
                const meta = chart.getDatasetMeta(datasetIndex)

                meta.data.forEach((bar, index) => {
                  const value = dataset.data[index]

                  if (value === null || value === undefined) {
                    return
                  }

                  ctx.save()
                  const isZero = Number(value) === 0

                  ctx.fillStyle = isZero ? 'rgba(0, 0, 0, 0.45)' : '#ffffff'
                  ctx.font = '600 11px Arial'
                  ctx.textAlign = 'center'
                  ctx.textBaseline = 'middle'
                  ctx.fillText(String(value), bar.x, isZero ? bar.base - 8 : bar.y + ((bar.base - bar.y) / 2))
                  ctx.restore()
                })
              })
            },
          },
        ],
      })
    }

    onMounted(() => {
      nextTick(renderChart)
    })

    onBeforeUnmount(() => {
      destroyChart()
    })

    watch(
        () => props.items,
        () => {
          nextTick(renderChart)
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
    }
  },
}
</script>

<style lang="scss" scoped>
.management-potential-levels-chart {
  width: 100%;
}

.management-potential-levels-chart__title {
  max-width: 500px;
  margin: 0 0 20px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);

  @media (max-width: $tablet-breakpoint) {
    max-width: unset;
  }
}

.management-potential-levels-chart__body {
  width: 100%;
  height: 245px;
}

@media (max-width: $mobile-breakpoint) {
  .management-potential-levels-chart__body {
    height: 240px;
  }
}
</style>
